
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, User, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AuthModal } from "./AuthModal";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useToast } from "@/hooks/use-toast";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useTranslation } from "react-i18next";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import Logo from "./Logo";
import SocialLinks from "./SocialLinks";
import LanguageSwitcher from "./LanguageSwitcher";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [openSheet, setOpenSheet] = useState(false);
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useTranslation();

  const avatarUrl = profile?.avatar_url ?? (user?.user_metadata?.avatar_url as string | undefined);
  const avatarFallback = (profile?.full_name || user?.email || "U").slice(0, 2).toUpperCase();

  useEffect(() => {
    setOpenSheet(false);
  }, [location]);

  const openAuthModal = () => {
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const handleSuccessfulAuth = () => {
    closeAuthModal();
    navigate("/profile");
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: t("auth.signoutSuccess"),
        description: t("auth.signoutSuccessMessage"),
      });
      navigate("/");
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("auth.signoutErrorMessage"),
        variant: "destructive",
      });
    }
  };

  const navigateToPage = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const isLinkActive = (path: string) => {
    if (path === "/services" && location.pathname.startsWith("/services")) {
      return true;
    }
    return location.pathname === path;
  };

  return (
    <nav className="sticky top-0 z-50 py-4 border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="container flex items-center justify-between">
        <Logo />

        <div className="hidden md:flex items-center justify-center gap-x-8 flex-1">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className={
                    isLinkActive("/services")
                      ? "bg-accent text-accent-foreground"
                      : ""
                  }
                >
                  {t("common.services")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                    {[
                      {
                        title: t("services.websites"),
                        path: "/services/websites",
                        icon: "🌐",
                      },
                      {
                        title: t("services.apps"),
                        path: "/services/apps",
                        icon: "📱",
                      },
                      {
                        title: t("services.ai"),
                        path: "/services/ai",
                        icon: "🤖",
                      },
                      {
                        title: t("services.cameras"),
                        path: "/services/cameras",
                        icon: "📷",
                      },
                      {
                        title: t("services.pc"),
                        path: "/services/pc",
                        icon: "💻",
                      },
                      {
                        title: t("services.courses"),
                        path: "/services/courses",
                        icon: "📚",
                      },
                    ].map((service) => (
                      <li key={service.path}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={service.path}
                            className={cn(
                              "block select-none gap-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                              isLinkActive(service.path)
                                ? "bg-accent text-accent-foreground"
                                : ""
                            )}
                          >
                            <div className="text-lg font-medium leading-none">
                              <span className="mr-2">{service.icon}</span>
                              {service.title}
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to="/portfolio"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/portfolio")
                      ? "bg-accent text-accent-foreground"
                      : ""
                  )}
                >
                  {t("common.portfolio")}
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to="/about"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/about")
                      ? "bg-accent text-accent-foreground"
                      : ""
                  )}
                >
                  {t("common.about")}
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to="/contact"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/contact")
                      ? "bg-accent text-accent-foreground"
                      : ""
                  )}
                >
                  {t("common.contact")}
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to="/blog"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/blog")
                      ? "bg-accent text-accent-foreground"
                      : ""
                  )}
                >
                  {t("common.blog")}
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <Link
                  to="/careers"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/careers")
                      ? "bg-accent text-accent-foreground"
                      : ""
                  )}
                >
                  {t("common.careers")}
                </Link>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

        </div>

        <div className="hidden md:flex items-center gap-x-3">
          <LanguageSwitcher />
          {user ? (
            <button
              type="button"
              onClick={() => navigate("/profile")}
              aria-label={t("auth.profile", "Profile")}
              className="rounded-full ring-offset-background transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Avatar className="h-9 w-9 border">
                <AvatarImage src={avatarUrl} alt={profile?.full_name ?? user.email ?? "avatar"} />
                <AvatarFallback>{avatarFallback}</AvatarFallback>
              </Avatar>
            </button>
          ) : (
            <Button variant="outline" size="sm" onClick={openAuthModal}>
              <User size={16} /> {t("auth.profile", "Profile")}
            </Button>
          )}
        </div>

        <div className="md:hidden flex items-center">
          <Sheet open={openSheet} onOpenChange={setOpenSheet}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Menu">
                <Menu size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm p-0">
              <div className="flex flex-col h-full">
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <Logo />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setOpenSheet(false)}
                    >
                      <X size={20} />
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-auto py-2 px-4">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 mt-2"
                    onClick={() => {
                      setOpenSheet(false);
                      if (user) {
                        navigate("/profile");
                      } else {
                        openAuthModal();
                      }
                    }}
                  >
                    {user ? (
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={avatarUrl} alt={profile?.full_name ?? user.email ?? "avatar"} />
                        <AvatarFallback className="text-[10px]">{avatarFallback}</AvatarFallback>
                      </Avatar>
                    ) : (
                      <User size={16} />
                    )}
                    <span>
                      {user
                        ? profile?.full_name || user.email || t("auth.profile", "Profile")
                        : t("auth.profile", "Profile")}
                    </span>
                  </Button>
                  <div className="gap-y-2 my-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">
                      {t("common.services")}
                    </h3>
                    <div className="gap-y-1">
                      {[
                        {
                          title: t("services.websites"),
                          path: "/services/websites",
                          icon: "🌐",
                        },
                        {
                          title: t("services.apps"),
                          path: "/services/apps",
                          icon: "📱",
                        },
                        {
                          title: t("services.ai"),
                          path: "/services/ai",
                          icon: "🤖",
                        },
                        {
                          title: t("services.cameras"),
                          path: "/services/cameras",
                          icon: "📷",
                        },
                        {
                          title: t("services.pc"),
                          path: "/services/pc",
                          icon: "💻",
                        },
                        {
                          title: t("services.courses"),
                          path: "/services/courses",
                          icon: "📚",
                        },
                      ].map((service) => (
                        <Link
                          key={service.path}
                          to={service.path}
                          className={cn(
                            "flex items-center justify-between py-2 px-4 rounded-full transition-colors hover:bg-accent",
                            isLinkActive(service.path)
                              ? "bg-accent text-accent-foreground"
                              : ""
                          )}
                          onClick={() => setOpenSheet(false)}
                        >
                          <div className="flex items-center gap-3">
                            <span className=" text-lg">{service.icon}</span>
                            <span>{service.title}</span>
                          </div>
                          <ChevronRight
                            size={16}
                            className="text-muted-foreground rtl:rotate-180"
                          />
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="gap-y-2 border-t pt-4 mt-4">
                    <Link
                      to="/portfolio"
                      className={cn(
                        "flex items-center justify-between py-2 px-4 rounded-full transition-colors hover:bg-accent",
                        isLinkActive("/portfolio")
                          ? "bg-accent text-accent-foreground"
                          : ""
                      )}
                      onClick={() => setOpenSheet(false)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg ">🗂️</span>
                        <span>{t("common.portfolio")}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-muted-foreground rtl:rotate-180"
                      />
                    </Link>
                  </div>
                  <div className="gap-y-2 border-t pt-4 mt-4">
                    <Link
                      to="/about"
                      className={cn(
                        "flex items-center justify-between py-2 px-4 rounded-full transition-colors hover:bg-accent",
                        isLinkActive("/about")
                          ? "bg-accent text-accent-foreground"
                          : ""
                      )}
                      onClick={() => setOpenSheet(false)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg ">ℹ️</span>
                        <span>{t("common.about")}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-muted-foreground rtl:rotate-180"
                      />
                    </Link>
                    <Link
                      to="/contact"
                      className={cn(
                        "flex items-center justify-between py-2 px-4 rounded-full transition-colors hover:bg-accent",
                        isLinkActive("/contact")
                          ? "bg-accent text-accent-foreground"
                          : ""
                      )}
                      onClick={() => setOpenSheet(false)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg ">🤗</span>
                        <span>{t("common.contact")}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-muted-foreground rtl:rotate-180"
                      />
                    </Link>
                    <Link
                      to="/blog"
                      className={cn(
                        "flex items-center justify-between py-2 px-4 rounded-full transition-colors hover:bg-accent",
                        isLinkActive("/blog")
                          ? "bg-accent text-accent-foreground"
                          : ""
                      )}
                      onClick={() => setOpenSheet(false)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📝</span>
                        <span>{t("common.blog")}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-muted-foreground rtl:rotate-180"
                      />
                    </Link>
                    <Link
                      to="/careers"
                      className={cn(
                        "flex items-center justify-between py-2 px-4 rounded-full transition-colors hover:bg-accent",
                        isLinkActive("/careers")
                          ? "bg-accent text-accent-foreground"
                          : ""
                      )}
                      onClick={() => setOpenSheet(false)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg ">💼</span>
                        <span>{t("common.careers")}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-muted-foreground rtl:rotate-180"
                      />
                    </Link>
                    <a
                      href="https://wa.me/905076421214"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between py-2 px-4 rounded-full transition-colors hover:bg-brand-yellow/10"
                      onClick={() => setOpenSheet(false)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg ">💬</span>
                        <span>{t("common.whatsapp")}</span>
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-muted-foreground rtl:rotate-180"
                      />
                    </a>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <LanguageSwitcher variant="mobile" />
                  </div>
                </div>

              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        onSuccess={handleSuccessfulAuth}
      />
    </nav>
  );
};

export default Navbar;
